///
/// EarthBrowser
///
/// Computed jet stream texture frame: a packed RGBA8 250 hPa wind field
/// (see JetStreamFieldGenerator) uploaded with linear filtering for the
/// jet shell shader.
///
/// Same lifecycle as PressureFieldComputedFrame: the worker's bytes are
/// held only until the layer persists them to the local sqlite database
/// (markPersisted), after which purged frames re-fetch on demand instead
/// of keeping ~1MB CPU copies per timestep in heap.
///
/// Created by Matt Giger on 10/23/2026
/// Copyright (c) 2026 Geodata Labs
///

import { TextureFrame } from "@/earthbrowser/datasets/TextureSeries"
import { type time, type timedelta } from "@stovokor/math/time"
import { ComponentType, InternalFormat, PixelFormat, TextureFilter, TextureWrap } from "@stovokor/shader/GLConstants"
import { Texture } from "@stovokor/shader/Texture"
import { type StovokorContext } from "@stovokor/StovokorContext"
import { SynopticTable } from "./SynopticTable"

export class JetStreamComputedFrame extends TextureFrame {
	/** Peak wind speed in this frame (m/s), for legends and labels */
	public readonly maxSpeedMps: number
	private fieldData?: Uint8Array
	private textureWidth: number
	private textureHeight: number
	private persisted = false
	/** Invalidates in-flight database fetches on purge/re-load */
	private fetchToken = 0

	constructor(
		context: StovokorContext,
		fieldData: Uint8Array,
		width: number,
		height: number,
		maxSpeedMps: number,
		startTime: time,
		duration: timedelta,
		frameId: string
	) {
		super(context, frameId, startTime, duration)
		this.fieldData = fieldData
		this.textureWidth = width
		this.textureHeight = height
		this.maxSpeedMps = maxSpeedMps
	}

	/** The database row for this frame is now queryable; drop the CPU copy */
	public markPersisted(): void {
		this.persisted = true
		this.fieldData = undefined
	}

	public override load(): void {
		if (this.loading || this.available) return

		if (this.fieldData) {
			this.uploadField(this.fieldData, this.textureWidth, this.textureHeight)
			return
		}

		if (!this.persisted) return

		this.loading = true
		const token = ++this.fetchToken
		SynopticTable.getJetField(this.url)
			.then(row => {
				if (token !== this.fetchToken || !row) return
				this.uploadField(row.field, row.width, row.height)
			})
			.catch(e => {
				console.warn(`[JetStreamComputedFrame] DB fetch failed for ${this.url}`, e)
			})
			.finally(() => {
				if (token === this.fetchToken) this.loading = false
			})
	}

	public override purge(): void {
		this.fetchToken++
		super.purge()
	}

	private uploadField(field: Uint8Array, width: number, height: number): void {
		this.createNewTexture()
		this.texture.uploadRawData(field, width, height, InternalFormat.RGBA8, PixelFormat.RGBA, ComponentType.UnsignedByte)
		this.available = true
	}

	protected override createNewTexture(): void {
		// Linear filtering: the shader reads u/v/speed as continuous fields.
		// Longitude wraps so streaks cross the dateline seamlessly.
		this.texture = new Texture(
			this.context,
			1,
			1,
			InternalFormat.RGBA8,
			PixelFormat.RGBA,
			ComponentType.UnsignedByte,
			TextureFilter.Linear,
			TextureFilter.Linear,
			TextureWrap.Repeat,
			TextureWrap.ClampToEdge
		)
	}
}
